// doctor-frontend/src/pages/intern/InternReviewQueue.jsx
// Legacy intern review queue for older InternActivity records.
// Reviewer Workbench remains the main approval surface.
// Comprehensive Services remains the main citizen / OPD operational hub.
//
// PATCH (Jul 2026, no regression):
// - Core Practice Area-safe display handling.
// - Tenant slug fallback from storage for stable navigation after redirects.
// - Robust reviewerQueue response parsing across items/rows/data/queue/results/list.
// - Status aliases for legacy and guided-observer variants.
// - Safer queue summaries for structured guided evidence while keeping the legacy queue flow.
// - Does not merge guided activity and OPD visit workflows.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import { useNavigate, useLocation } from 'react-router-dom';
import { internApi } from '../../services/internApi';

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';
const STRUCTURED_MARKER_KEY = '__structured__';

const STATUSES = [
  {
    value: 'submitted,returned,observed-submitted,observed-returned',
    label: 'Pending / Returned',
  },
  { value: 'submitted,observed-submitted', label: 'Submitted' },
  { value: 'returned,observed-returned,observed-repeat,repeat', label: 'Returned / Repeat' },
  { value: 'approved,observed-approved', label: 'Approved' },
  { value: 'draft', label: 'Draft' },
];

function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = ['Entrustable', 'Professional', 'Activities'].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value || 'null');
  } catch {
    return null;
  }
}

function coerceList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.queue)) return payload.queue;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.activities)) return payload.activities;
  if (Array.isArray(payload?.list)) return payload.list;
  return [];
}

function readTenantSlugFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem('tenantSlug'),
      localStorage.getItem('activeTenantSlug'),
      sessionStorage.getItem('tenantSlug'),
      sessionStorage.getItem('activeTenantSlug'),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    [
      safeJsonParse(localStorage.getItem('loggedInUser')),
      safeJsonParse(sessionStorage.getItem('loggedInUser')),
      safeJsonParse(localStorage.getItem('user')),
      safeJsonParse(sessionStorage.getItem('user')),
      safeJsonParse(localStorage.getItem('currentUser')),
      safeJsonParse(sessionStorage.getItem('currentUser')),
    ]
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantSlug,
          obj?.tenantSlug,
          core?.tenant?.slug,
          obj?.tenant?.slug
        );
      });
  } catch {
    // ignore storage failures
  }

  return candidates
    .map((value) => String(value || '').trim())
    .find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || '';
}

function persistTenantSlug(slug) {
  try {
    const clean = String(slug || '').trim();
    if (!clean) return;
    localStorage.setItem('tenantSlug', clean);
    localStorage.setItem('activeTenantSlug', clean);
    sessionStorage.setItem('tenantSlug', clean);
    sessionStorage.setItem('activeTenantSlug', clean);
  } catch {
    // ignore storage failures
  }
}

function tenantPrefixFromPathname(pathname) {
  try {
    const m = String(pathname || '').match(/^\/t\/[^/]+/);
    if (m) {
      const slug = m[0].replace(/^\/t\//, '');
      persistTenantSlug(slug);
      return m[0];
    }
  } catch {
    // ignore
  }

  const slug = readTenantSlugFromStorage();
  return slug ? `/t/${slug}` : '';
}

function withTenant(pathname, path) {
  const p = String(path || '/');
  if (/^\/t\/[^/]+(\/|$)/.test(p)) return p;

  const tenant = tenantPrefixFromPathname(pathname);
  const clean = p.startsWith('/') ? p : `/${p}`;
  return tenant ? `${tenant}${clean}`.replace(/\/{2,}/g, '/') : clean;
}

function normalizeStatusToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-');
}

function statusLabel(value) {
  const s = normalizeStatusToken(value);
  if (s === 'observed-submitted' || s === 'submitted' || s === 'pending') return 'Submitted';
  if (s === 'observed-returned' || s === 'returned' || s === 'returned-to-edit') return 'Returned';
  if (s === 'observed-repeat' || s === 'repeat' || s === 'repeat-required') return 'Repeat required';
  if (s === 'observed-approved' || s === 'approved') return 'Approved';
  if (s === 'draft') return 'Draft';
  return sanitizeDisplayText(value) || '-';
}

function statusChipColor(value) {
  const s = normalizeStatusToken(value);
  if (s.includes('approve')) return 'success';
  if (s.includes('submit') || s === 'pending') return 'warning';
  if (s.includes('return') || s.includes('repeat')) return 'default';
  return 'default';
}

function coerceRow(row) {
  if (typeof row === 'string') return { description: row };
  if (!row || typeof row !== 'object') return { description: String(row ?? '') };
  return row;
}

function parseStructuredValue(raw) {
  if (raw == null || raw === '') return { plain: '', extra: null };

  if (typeof raw !== 'string') {
    if (typeof raw === 'object' && raw[STRUCTURED_MARKER_KEY]) {
      const body = raw[STRUCTURED_MARKER_KEY] || {};
      const { __notes, ...rest } = body;
      return {
        plain: sanitizeDisplayText(__notes || ''),
        extra: rest && Object.keys(rest).length ? rest : null,
      };
    }

    try {
      return { plain: sanitizeDisplayText(JSON.stringify(raw)), extra: null };
    } catch {
      return { plain: sanitizeDisplayText(String(raw)), extra: null };
    }
  }

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed[STRUCTURED_MARKER_KEY]) {
      const body = parsed[STRUCTURED_MARKER_KEY] || {};
      const { __notes, ...rest } = body;
      return {
        plain: sanitizeDisplayText(__notes || ''),
        extra: Object.keys(rest || {}).length ? rest : null,
      };
    }
  } catch {
    // not JSON; keep raw text
  }

  return { plain: sanitizeDisplayText(raw), extra: null };
}

function prettyLabel(key) {
  if (!key) return '';
  const withSpaces = String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return sanitizeDisplayText(withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1));
}

function summarizeStructuredExtra(extra) {
  if (!extra || typeof extra !== 'object') return '';

  const pieces = Object.entries(extra)
    .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
    .slice(0, 4)
    .map(([key, value]) => `${prettyLabel(key)}: ${sanitizeDisplayText(value)}`);

  return pieces.join(' • ');
}

function buildSummaryFromItems(it) {
  const rows = Array.isArray(it.items)
    ? it.items
    : Array.isArray(it.stepData)
    ? it.stepData
    : Array.isArray(it.stepsData)
    ? it.stepsData
    : [];

  const pieces = [];

  rows.slice(0, 4).forEach((row) => {
    const r = coerceRow(row);

    let text = sanitizeDisplayText(r.description || r.text || r.label || r.title || '');

    if (!text && typeof r.valueText === 'string') {
      const { plain, extra } = parseStructuredValue(r.valueText);
      text = plain || summarizeStructuredExtra(extra);
    }

    if (!text && typeof r.notes === 'string') {
      const { plain, extra } = parseStructuredValue(r.notes);
      text = plain || summarizeStructuredExtra(extra);
    }

    if (!text && r.answer !== undefined) {
      text = sanitizeDisplayText(r.answer);
    } else if (!text && r.value !== undefined) {
      text = sanitizeDisplayText(r.value);
    }

    if (text) pieces.push(text);
  });

  if (pieces.length) return pieces.join(' • ');

  const slug = String(it.templateSlug || it.slug || it.activityKey || '').toLowerCase();
  const name = String(it.templateTitle || it.activityTitle || it.title || it.templateName || '').toLowerCase();
  const isNvd =
    slug === 'nvd' ||
    name.includes('normal vaginal') ||
    name.includes('normal delivery');

  if (isNvd) {
    return 'Conduct Normal Delivery — structured guided activity (NVD)';
  }

  const meta = it.summary || it.internNotes || it.notes || it.description || '';

  if (typeof meta === 'string' && meta.trim()) {
    const { plain, extra } = parseStructuredValue(meta);
    return plain || summarizeStructuredExtra(extra) || sanitizeDisplayText(meta);
  }

  return 'No description available yet';
}

function getActivityId(item) {
  return item?._id || item?.id || item?.activityId || item?.activityInstanceId || item?.instanceId || '';
}

function rowTitle(item) {
  return sanitizeDisplayText(
    item?.templateTitle ||
      item?.activityTitle ||
      item?.title ||
      item?.templateName ||
      item?.activityType ||
      item?.template?.title ||
      item?.template?.name ||
      'Review item'
  );
}

function rowInternName(item) {
  return sanitizeDisplayText(
    item?.internName ||
      item?.userName ||
      item?.userEmail ||
      item?.userId ||
      item?.createdByName ||
      item?.submittedByName ||
      item?.createdBy?.name ||
      item?.user?.name ||
      '—'
  );
}

function rowCorePracticeArea(item) {
  const target = item?.reportingTarget && typeof item.reportingTarget === 'object'
    ? item.reportingTarget
    : {};

  return sanitizeDisplayText(
    item?.corePracticeAreaTitle ||
      item?.practiceAreaTitle ||
      item?.finalGroup ||
      target.section ||
      ''
  );
}

function rowRotationItem(item) {
  const target = item?.reportingTarget && typeof item.reportingTarget === 'object'
    ? item.reportingTarget
    : {};

  return sanitizeDisplayText(target.item || item?.rotationItem || item?.activityLabel || '');
}
function rowDateLabel(item) {
  const value = item?.date || item?.activityDate || item?.createdAt || item?.updatedAt || '';
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return sanitizeDisplayText(value);
  return d.toDateString();
}

function isGuidedLike(item) {
  const status = normalizeStatusToken(item?.status || item?.workflowStatus || item?.approvalStatus || '');
  return Boolean(
    item?.templateTitle ||
      item?.templateSlug ||
      item?.activityInstanceId ||
      item?.reportingTarget ||
      item?.sourceFlow === 'guided' ||
      status.startsWith('observed-')
  );
}

function decorateRows(rows) {
  return coerceList(rows).map((row) => {
    const item = row && typeof row === 'object' ? row : { description: String(row ?? '') };
    return {
      ...item,
      __displayTitle: rowTitle(item),
      __summary: buildSummaryFromItems(item),
      __status: statusLabel(item?.status || item?.workflowStatus || item?.approvalStatus),
      __corePracticeArea: rowCorePracticeArea(item),
      __rotationItem: rowRotationItem(item),
      __isGuidedLike: isGuidedLike(item),
    };
  });
}

export default function InternReviewQueue() {
  const navigate = useNavigate();
  const location = useLocation();

  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);

  const [from, setFrom] = useState(
    iso(new Date(today.getFullYear(), today.getMonth(), 1))
  );
  const [to, setTo] = useState(
    iso(new Date(today.getFullYear(), today.getMonth() + 1, 0))
  );
  const [status, setStatus] = useState('submitted,returned,observed-submitted,observed-returned');
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const fromPath = useMemo(() => {
    try {
      return `${location.pathname || ''}${location.search || ''}${location.hash || ''}`;
    } catch {
      return '';
    }
  }, [location.pathname, location.search, location.hash]);

  const returnedCount = useMemo(
    () => items.filter((it) => normalizeStatusToken(it?.status || it?.workflowStatus || it?.approvalStatus).includes('return')).length,
    [items]
  );

  const submittedCount = useMemo(
    () => items.filter((it) => {
      const s = normalizeStatusToken(it?.status || it?.workflowStatus || it?.approvalStatus);
      return s.includes('submit') || s === 'pending';
    }).length,
    [items]
  );

  const load = useCallback(async () => {
    try {
      setErr('');
      setLoading(true);
      const res = await internApi.reviewerQueue({
        from,
        to,
        status,
        page: 1,
        limit: 50,
        cb: Date.now(),
      });
      setItems(decorateRows(res));
    } catch (e) {
      setErr(e?.message || 'Failed to load queue');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, status]);

  useEffect(() => {
    load();
  }, [load]);

  const goToReview = (item) => {
    const id = getActivityId(item);
    if (!id) {
      setErr('Cannot open item: missing review ID.');
      return;
    }

    // Guided activity evidence has a dedicated observer/evidence page.
    // Legacy intern activity review keeps the old route for compatibility.
    const target = item?.__isGuidedLike
      ? withTenant(location.pathname, `/intern/activity/observe/${encodeURIComponent(id)}`)
      : withTenant(location.pathname, `/intern/review/${encodeURIComponent(id)}`);

    navigate(target, {
      state: {
        from: fromPath,
        viewOnly: false,
      },
    });
  };

  const goReviewerWorkbench = () => {
    navigate(withTenant(location.pathname, '/dashboard/approval'));
  };

  const goComprehensiveServices = () => {
    navigate(withTenant(location.pathname, '/dashboard/operations'));
  };

  const goActivityLauncher = () => {
    navigate(withTenant(location.pathname, '/intern/activity/new'));
  };

  return (
    <Box className="space-y-4 p-4 md:p-6">
      <Box className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <Box>
          <Typography variant="h5" className="font-semibold">
            Legacy Intern Review Queue
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
            Use this page as a focused reviewer queue for older intern activity review items.
            Reviewer Workbench remains the main approval surface, Activity Launcher remains the
            guided-activity start surface, and Comprehensive Services remains the citizen / OPD hub.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={<LaunchIcon />} onClick={goReviewerWorkbench}>
            Reviewer Workbench
          </Button>
          <Button size="small" variant="outlined" startIcon={<LaunchIcon />} onClick={goActivityLauncher}>
            Activity Launcher
          </Button>
          <Button size="small" variant="outlined" onClick={goComprehensiveServices}>
            Comprehensive Services
          </Button>
        </Stack>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Status"
            select
            size="small"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: { md: 260 } }}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Apply / Refresh'}
          </Button>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Chip label={`Total: ${items.length}`} variant="outlined" />
        <Chip label={`Submitted: ${submittedCount}`} color={submittedCount ? 'warning' : 'default'} variant={submittedCount ? 'filled' : 'outlined'} />
        <Chip label={`Returned: ${returnedCount}`} color={returnedCount ? 'default' : 'default'} variant="outlined" />
      </Stack>

      {err && (
        <Alert severity="error" onClose={() => setErr('')}>
          {err}
        </Alert>
      )}

      <Divider />

      {loading && (
        <Box className="flex justify-center p-6">
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && items.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ opacity: 0.65 }}>
            No items match the filters.
          </Typography>
        </Paper>
      )}

      {!loading && items.length > 0 && (
        <Stack spacing={1.5}>
          {items.map((it, index) => {
            const id = getActivityId(it);
            const dateLabel = rowDateLabel(it);
            const centre = sanitizeDisplayText(it.centre || it.chc || it.location || '—');
            const posting = sanitizeDisplayText(it.posting || it.postingBlock || it.rotation || '—');
            const internName = rowInternName(it);
            const statusRaw = it.status || it.workflowStatus || it.approvalStatus || '';
            const coreArea = it.__corePracticeArea;
            const rotationItem = it.__rotationItem;
            const summary = it.__summary;
            const key = id || `${dateLabel}-${centre}-${index}`;

            return (
              <Paper
                key={key}
                variant="outlined"
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                role="button"
                tabIndex={0}
                onClick={() => goToReview(it)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    goToReview(it);
                  }
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 0.5 }}>
                      <Chip size="small" label={statusLabel(statusRaw)} color={statusChipColor(statusRaw)} />
                      {it.__isGuidedLike && <Chip size="small" variant="outlined" label="Guided" />}
                      {coreArea && <Chip size="small" variant="outlined" label={`${CORE_PRACTICE_AREA_LABEL}: ${coreArea}`} />}
                    </Stack>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {it.__displayTitle}
                    </Typography>

                    <Typography variant="caption" sx={{ opacity: 0.72 }}>
                      {dateLabel} • {centre} • {posting}
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Intern: <b>{internName}</b>
                    </Typography>

                    {rotationItem && (
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.72 }}>
                        Rotation item: {rotationItem}
                      </Typography>
                    )}

                    <Tooltip title={summary || ''} disableHoverListener={!summary || summary.length < 120}>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.75,
                          opacity: 0.88,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {summary}
                      </Typography>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={normalizeStatusToken(statusRaw).includes('return') ? <ReplayIcon /> : <VisibilityIcon />}
                      onClick={(event) => {
                        event.stopPropagation();
                        goToReview(it);
                      }}
                    >
                      {it.__isGuidedLike ? 'Open Evidence' : 'Open'}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
